import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AssetService, AssetTypeService } from '../../services/asset/asset-api-service';
import { AssetType, FormValidator } from '../../services/asset/asset-models';
import { getCurrentFormConfigs } from '../../services/languageService';
import { useAssetsStore } from '../../hooks/useAssetsStore';
import { loadAssetsThunk } from '../../store/assets/assetsThunks';
import { AppDispatch } from '../../store';
import './CreateAsset.css';
import { HomeButton } from "../../components/homeButton/homeButton";
import { useTranslation } from 'react-i18next';
import { getFormCategoryFromSystemType } from '../../types';


const CreateAsset: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    // Get parent ID from URL params
    const searchParams = new URLSearchParams(location.search);
    const parentIdFromUrl = searchParams.get('parent');
    const parentsParam = searchParams.get('parents');
    
    // Helper function to get immediate parent
    const getImmediateParent = () => {
        if (parentIdFromUrl) return parseInt(parentIdFromUrl);
        if (parentsParam) {
            const parentIds = parentsParam.split(',');
            return parseInt(parentIds[parentIds.length - 1]);
        }
        return 0;
    };

    const initialParentId = getImmediateParent();

    const {
        loading: assetsLoading,
        loaded: assetsLoaded,
        getAssetById
    } = useAssetsStore();

    const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<number>(0);
    const [formData, setFormData] = useState<any>({});
    const [selectedParentId, setSelectedParentId] = useState<number>(initialParentId);

    const FORM_CONFIGS = getCurrentFormConfigs();

    // Get the parent asset info if parentId is provided
    const parentAsset = initialParentId > 0 ? getAssetById(initialParentId) : null;

    // Determine form category from selected asset type
    const selectedFormType = useMemo(() => {
        if (selectedAssetTypeId === 0) return '';

        const selectedAssetType = assetTypes.find(type => type.id === selectedAssetTypeId);
        if (!selectedAssetType) return '';

        return getFormCategoryFromSystemType(selectedAssetType.assetSystemType);
    }, [selectedAssetTypeId, assetTypes]);

    // Load asset types and ensure assets are loaded
    useEffect(() => {
        loadAssetTypes();

        if (!assetsLoaded && !assetsLoading) {
            dispatch(loadAssetsThunk());
        }
    }, [dispatch, assetsLoaded, assetsLoading]);

    // Reset form data when asset type changes
    useEffect(() => {
        if (selectedAssetTypeId > 0) {
            setFormData({});
        }
    }, [selectedAssetTypeId]);

    const getFormDisplayName = (formType: string) => {
        const names = {
            'building': t('admin:assets.buildingTab', 'Building'),
            'solar': t('admin:assets.solarPanelTab', 'Solar Panel'),
            'solar_inverter': t('admin:assets.assetSystemTypeEnum.SOLARINVERTER', 'Solar Panel Inverter'),
            'solar_string': t('admin:assets.assetSystemTypeEnum.SOLARSTRING', 'Solar String'),
            'ventilation': t('ventilation', 'Ventilation'),
            'heat_pump': t('admin:assets.assetSystemTypeEnum.HEATPUMP', 'Heat Pump'),
            'boiler': t('admin:assets.assetSystemTypeEnum.BOILER', 'Boiler'),
            'distribution_board': t('admin:assets.assetSystemTypeEnum.DISTRIBUTIONBOARD', 'Distribution Board'),
            'heat_exchanger': t('admin:assets.heatExchangerTab', 'Heat Exchanger'),
            'street_light': t('admin:assets.assetSystemTypeEnum.STREETLIGHT', 'Street Light'),
            'street_light_junction_box': t('admin:assets.assetSystemTypeEnum.STREETLIGHTJUNCTIONBOX', 'Street Light Junction Box'),
            'building_automation': t('admin:assets.assetSystemTypeEnum.BUILDINGAUTOMATION', 'Building Automation')
        };
        return names[formType] || 'Asset';
    };

    const loadAssetTypes = async () => {
        try {
            setLoading(true);
            const types = await AssetTypeService.getAssetTypes();
            setAssetTypes(types);
        } catch (error) {
            setMessage({ type: 'error', text: `Failed to load asset types: ${error}` });
            console.error('Error loading asset types:', error);
        } finally {
            setLoading(false);
        }
    };

    // Group asset types by category for better UX (excluding building category)
    const groupedAssetTypes = useMemo(() => {
        const groups: { [key: string]: AssetType[] } = {};

        assetTypes.forEach(assetType => {
            const category = getFormCategoryFromSystemType(assetType.assetSystemType);
            if (category !== 'unknown' && category !== 'building') {
                if (!groups[category]) {
                    groups[category] = [];
                }
                groups[category].push(assetType);
            }
        });

        return groups;
    }, [assetTypes]);

    const handleDynamicSubmit = async (e: React.FormEvent, createWithCircuits: boolean = false) => {
        e.preventDefault();
        try {
            setLoading(true);
            const completeFormData = {
                ...formData,
                assetTypeId: selectedAssetTypeId,
                ...(selectedParentId > 0 && { parentId: selectedParentId })
            };

            if (selectedFormType === 'distribution_board') {
                completeFormData.assetDistributionBoard = {
                    ...completeFormData.assetDistributionBoard,
                    distributionBoardCircuits: []
                };
            }

            const validation = FormValidator.validateForm(completeFormData, FORM_CONFIGS[selectedFormType]);
            if (!validation.isValid) {
                console.log('Form validation failed:', validation.errors);
                setMessage({ type: 'error', text: `Validation failed: ${validation.errors.join(', ')}` });
                return;
            }

            const newAsset = await AssetService.createAssetFromForm(completeFormData, selectedFormType);

            if (createWithCircuits && selectedFormType === 'distribution_board') {
                navigate(`/interpret-json?assetId=${newAsset.id}`);
            } else {
                setMessage({
                    type: 'success',
                    text: `${getFormDisplayName(selectedFormType)} "${newAsset?.name || 'New Asset'}" created successfully!`
                });

                // Reset form
                setFormData({});
                setSelectedAssetTypeId(0);
                setSelectedParentId(initialParentId);

                // Refresh Redux store
                dispatch(loadAssetsThunk());

                // Navigate back to parent asset if we came from one
                if (initialParentId > 0) {
                    const searchParams = new URLSearchParams(location.search);
                    const parentsParam = searchParams.get('parents');

                    const backUrl = parentsParam
                        ? `/assets/${initialParentId}?parents=${parentsParam}`
                        : `/assets/${initialParentId}`;
                    navigate(backUrl);
                }
            }

        } catch (error: any) {
            let errorMessage = 'Failed to create asset';
            if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            } else if (error.response?.data?.errorString) {
                errorMessage += `: ${error.response.data.errorString}`;
            } else if (error.message) {
                errorMessage += `: ${error.message}`;
            }

            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="create-asset-container">
            <HomeButton />

            {message && (
                <div className={message.type === 'success' ? 'success' : 'error'}>
                    {message.text}
                </div>
            )}

            {/* Parent Asset Display */}
            <div className="section">
                <div className="form-group">
                    <label className="label">{t('parentAsset', 'Parent Asset')}:</label>
                    {assetsLoading ? (
                        <div className="loading-parents">
                            <div className="loading-spinner"></div>
                            <span>{t('loading', 'Loading...')}</span>
                        </div>
                    ) : parentAsset ? (
                        <div className="parent-option">
                            <div className="parent-name">
                                {parentAsset.name || 'Unnamed Asset'}
                            </div>
                            <div className="parent-type">
                                {parentAsset.assetType?.name || 'Unknown Type'}
                            </div>
                            <div className="selected-indicator">✓</div>
                        </div>
                    ) : (
                        <div className="info">
                            No parent asset selected. This will be a root asset.
                        </div>
                    )}
                </div>
            </div>

            {/* Asset Type Selector */}
            <div className="section-ca">
                <div className="form-group">
                    <label className="label">{t('admin:assets.selectAssetType', 'Select Asset Type')}</label>
                    <select
                        className="select"
                        value={selectedAssetTypeId}
                        onChange={(e) => setSelectedAssetTypeId(parseInt(e.target.value))}
                        required
                    >
                        <option value={0}>{t('admin:assets.selectAssetType', 'Select Asset Type')}</option>
                        {Object.entries(groupedAssetTypes).map(([category, types]) => (
                            <optgroup key={category} label={getFormDisplayName(category)}>
                                {types.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>
            </div>

            {/* Dynamic Form */}
            {selectedFormType && FORM_CONFIGS[selectedFormType] && (
                <div className="section">
                    <form className="form" onSubmit={(e) => handleDynamicSubmit(e, false)}>
                        {/* Dynamic Form Fields */}
                        {FORM_CONFIGS[selectedFormType].map((field) => (
                            <div key={field.key} className="form-group">
                                <label className="label">
                                    {field.label} {field.required && '*'}:
                                </label>

                                {field.type === 'textarea' ? (
                                    <textarea
                                        className="input textarea"
                                        value={formData[field.key] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                    />
                                ) : field.type === 'select' ? (
                                    <select
                                        className="select"
                                        value={formData[field.key] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                                        required={field.required}
                                    >
                                        <option value="">{t('select', 'Select')} {field.label}</option>
                                        {field.options?.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        className="input"
                                        type={field.type}
                                        value={formData[field.key] || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            [field.key]: field.type === 'number'
                                                ? (e.target.value ? parseFloat(e.target.value) : undefined)
                                                : e.target.value
                                        })}
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        min={field.min}
                                        max={field.max}
                                        step={field.type === 'number' ? 'any' : undefined}
                                    />
                                )}

                                {field.unit && <small className="field-unit">{field.unit}</small>}
                            </div>
                        ))}

                        {/* Submit Buttons */}
                        {selectedFormType === 'distribution_board' ? (
                            <div className="distribution-board-buttons">
                                <button
                                    className="button"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? t('creating', 'Creating...') : `${t('create', 'Create')} ${t('admin:assets.assetSystemTypeEnum.DISTRIBUTIONBOARD', 'Distribution Board')}`}
                                </button>

                                <button
                                    className="button button-primary"
                                    type="button"
                                    onClick={(e) => handleDynamicSubmit(e, true)}
                                    disabled={loading}
                                >
                                    {loading ? t('creating', 'Creating...') : `${t('create', 'Create')} ${t('admin:assets.assetSystemTypeEnum.DISTRIBUTIONBOARD', 'Distribution Board')} + ${t('add', 'Add')} ${t('circuits', 'Circuits')}`}
                                </button>
                            </div>
                        ) : (
                            <button
                                className="button"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? t('creating', 'Creating...') : `${t('admin:organization.saveSettings')}`}
                            </button>
                        )}
                    </form>
                </div>
            )}
        </div>
    );
};

export default CreateAsset;